import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { API_BASE, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, CurrencyInr } from "@phosphor-icons/react";
import { InlineQR } from "@/components/QRCodeButton";
import UpiPayQR from "@/components/UpiPayQR";
import { SupportingTendersView } from "@/components/SupportingTendersEditor";
import { toast } from "sonner";

export default function PublicInvoice() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/public/invoices/${id}?token=${encodeURIComponent(token || "")}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setError("This invoice link is invalid or has expired."));
  }, [id, token]);

  // Auto-fire the browser Print dialog when opened with ?autoprint=1
  const autoprint = params.get("autoprint") === "1";
  useEffect(() => {
    if (data && autoprint) {
      const t = setTimeout(() => window.print(), 350);
      return () => clearTimeout(t);
    }
  }, [data, autoprint]);

  const payNow = async () => {
    setPayLoading(true);
    try {
      const r = await fetch(`${API_BASE}/public/invoices/${id}/payment-link?token=${encodeURIComponent(token || "")}`, { method: "GET" });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not create payment link");
      }
      const j = await r.json();
      if (j.short_url) {
        window.location.href = j.short_url;
      } else {
        throw new Error("No payment link returned");
      }
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setPayLoading(false);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;

  const inv = data.invoice;
  const company = data.company || {};
  const intra = (inv.cgst_total || 0) + (inv.sgst_total || 0) > 0;
  const balanceDue = Number(inv.balance_due || 0);

  return (
    <div className="min-h-screen bg-white text-black" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="max-w-4xl mx-auto p-10 print:p-6">
        <div className="flex justify-end mb-6 print:hidden gap-2">
          {balanceDue > 0 && (
            <Button
              onClick={payNow}
              disabled={payLoading}
              className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5"
              data-testid="public-invoice-pay-now"
            >
              <CurrencyInr size={16} weight="bold" className="mr-1.5" />
              {payLoading ? "Opening payment…" : `Pay ${inr(balanceDue)} Now`}
            </Button>
          )}
          <Button onClick={() => window.print()} className="h-10" variant="outline"><Printer size={14} className="mr-1.5" /> Print</Button>
        </div>

        <div className="border-2 border-black">
          <div className="p-6 border-b-2 border-black flex items-center justify-between gap-6">
            <img
              src={company.logo_url || "/brand/vetrich-logo.png"}
              alt={company.name || "Logo"}
              style={{ height: "96px", width: "auto", objectFit: "contain", flexShrink: 0 }}
              crossOrigin="anonymous"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Tax Invoice</div>
              <h1 className="text-2xl font-bold tracking-tight mt-1" style={{ fontFamily: "'Chivo', sans-serif" }}>{company.name || "Vetrich"}</h1>
              <div className="text-[12px] text-gray-600 mt-1.5 whitespace-pre-line">{company.address}</div>
              <div className="text-[12px] mt-1">GSTIN: <span className="font-mono">{company.gstin}</span> · State: {company.state} ({company.state_code})</div>
              <div className="text-[12px]">Phone: {company.phone} · Email: {company.email}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Invoice #</div>
              <div className="text-lg font-mono font-bold mt-1">{inv.number}</div>
              <div className="mt-2 text-[12px]">Date: <span className="font-mono">{inv.date}</span></div>
              <div className="mt-3 flex justify-end gap-3 items-start">
                {inv.share_token && (
                  <InlineQR
                    url={`${window.location.origin}/p/invoice/${inv.id}?t=${inv.share_token}`}
                    size={68}
                    caption="Scan"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-2 gap-8 border-b-2 border-black">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Bill To</div>
              <div className="font-bold text-base" data-testid="bill-to-name">{inv.customer_name}</div>
              {inv.customer_phone && <div className="text-[12px] mt-1" data-testid="bill-to-phone">Mobile: <span className="font-mono">{inv.customer_phone}</span></div>}
              {inv.customer_address && <div className="text-[12px] mt-0.5 whitespace-pre-line" data-testid="bill-to-address">{inv.customer_address}</div>}
              {inv.customer_gstin && <div className="text-[12px] mt-1">GSTIN: <span className="font-mono">{inv.customer_gstin}</span></div>}
              {inv.customer_state_code && <div className="text-[12px]">State Code: <span className="font-mono">{inv.customer_state_code}</span></div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5" style={{ fontFamily: "'Chivo', sans-serif" }}>Payment</div>
              <div className="text-[13px]">Status: <span className="font-semibold uppercase">{inv.status}</span></div>
              <div className="text-[13px]">Paid: <span className="font-mono">{inr(inv.paid_amount)}</span></div>
              {(inv.payment_splits || []).length > 0 ? (() => {
                const uniqueModes = Array.from(new Set(
                  inv.payment_splits.map((sp) => (sp.mode || "").trim().toUpperCase().replace(/_/g, " "))
                )).filter(Boolean);
                return (
                  <>
                    <div className="text-[13px]">
                      Modes: <span className="font-semibold font-mono">{uniqueModes.join(" · ")}</span>
                    </div>
                    <div className="mt-1 border border-gray-300 rounded">
                      <table className="w-full text-[11.5px]">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left px-2 py-1 font-semibold">Mode</th>
                            <th className="text-right px-2 py-1 font-semibold">Amount</th>
                            <th className="text-left px-2 py-1 font-semibold">UPI ID / Transaction ID / UTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inv.payment_splits.map((sp, i) => (
                            <tr key={i} className="border-t border-gray-200">
                              <td className="px-2 py-1 uppercase">{String(sp.mode || "").replace(/_/g, " ")}</td>
                              <td className="px-2 py-1 font-mono text-right">{inr(sp.amount || 0)}</td>
                              <td className="px-2 py-1 font-mono text-gray-600">{sp.reference || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })() : (inv.payment_mode || "").trim() ? (
                <div className="text-[13px]">
                  Mode: <span className="font-semibold uppercase">{String(inv.payment_mode).replace(/_/g, " ")}</span>
                  {inv.payment_reference ? <span className="font-mono text-gray-600"> · {inv.payment_reference}</span> : null}
                </div>
              ) : null}
              <div className="text-[13px]">Balance Due: <span className="font-mono font-semibold">{inr(inv.balance_due)}</span></div>
            </div>
          </div>

          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b-2 border-black" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">#</th>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">Description</th>
                <th className="text-left p-3 uppercase text-[10px] tracking-widest">HSN</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Qty</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Rate</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Tax</th>
                <th className="text-right p-3 uppercase text-[10px] tracking-widest">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={it.id || it.product_id || `${it.hsn}-${it.name}-${i}`} className="border-b border-gray-300">
                  <td className="p-3 font-mono">{i + 1}</td>
                  <td className="p-3">{it.name}</td>
                  <td className="p-3 font-mono">{it.hsn}</td>
                  <td className="p-3 text-right font-mono">{it.quantity} {it.unit}</td>
                  <td className="p-3 text-right font-mono">{inr(it.rate)}</td>
                  <td className="p-3 text-right font-mono">{it.tax_rate}%</td>
                  <td className="p-3 text-right font-mono font-semibold">{inr(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t-2 border-black flex">
            <div className="flex-1 p-6 text-[12px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Notes / Work Name</div>
              <div className="whitespace-pre-line">{inv.notes || "—"}</div>
            </div>
            <div className="w-72 border-l-2 border-black p-6 text-[13px] space-y-1.5">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-mono">{inr(inv.subtotal)}</span></div>
              {intra ? (
                <>
                  <div className="flex justify-between"><span className="text-gray-600">CGST</span><span className="font-mono">{inr(inv.cgst_total)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">SGST</span><span className="font-mono">{inr(inv.sgst_total)}</span></div>
                </>
              ) : (
                <div className="flex justify-between"><span className="text-gray-600">IGST</span><span className="font-mono">{inr(inv.igst_total)}</span></div>
              )}
              <div className="border-t-2 border-black pt-2 mt-2 flex justify-between font-bold text-base" style={{ fontFamily: "'Chivo', sans-serif" }}>
                <span>Grand Total</span><span className="font-mono">{inr(inv.grand_total)}</span>
              </div>
            </div>
          </div>

          <div className="p-6 border-t-2 border-black">
            <SupportingTendersView items={inv.supporting_tenders} />
          </div>

          <div className="p-6 border-t-2 border-black flex items-end justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              {balanceDue > 0 && company.upi_id && (
                <UpiPayQR
                  vpa={company.upi_id}
                  payeeName={company.name}
                  amount={balanceDue}
                  reference={inv.number}
                  size={82}
                  caption="Pay UPI"
                />
              )}
              {balanceDue <= 0 && (
                <div className="border-2 border-emerald-600 rounded-md bg-emerald-50 p-2 text-center min-w-[82px]">
                  <div className="h-9 w-9 rounded-full bg-emerald-600 mx-auto flex items-center justify-center">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.16em] text-emerald-700 font-bold mt-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
                    Fully Paid
                  </div>
                </div>
              )}
              <div className="text-[11px] text-gray-600 self-end">This is a computer-generated invoice. E&amp;OE.</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>Authorised Signatory</div>
              <div className="mt-8 text-[12px] font-semibold">For {company.name}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-gray-500" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Powered by {company.name || "Vetrich Technologies"} · Smart Accounting. Smarter Business.
        </div>
      </div>
    </div>
  );
}
